import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Fix 1: Removed unused 'faEdit' import
import { faTrash, faSearch, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import serverURL from '../config';

const AssigneeFeedbackList = ({ eventId, levelId }) => {
  const STORAGE_KEY_ASSIGNEES = `feedback_assignees_${eventId}_${levelId}`;
  const STORAGE_KEY_FEEDBACK = `feedback_responses_${eventId}_${levelId}`;

  const [candidates, setCandidates] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateFeedback, setCandidateFeedback] = useState({});
  const [isAssigneeModalOpen, setIsAssigneeModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [newAssignee, setNewAssignee] = useState({
    interviewer_name: '',
    interviewer_email: '',
    interview_type: 'Online',
  });
  const [feedbackForm, setFeedbackForm] = useState({
    feedback_title: '',
    feedback_text: '',
    rating: 3,
  });

  const loadData = useCallback(() => {
    try {
      const storedAssignees = localStorage.getItem(STORAGE_KEY_ASSIGNEES);
      const storedFeedback = localStorage.getItem(STORAGE_KEY_FEEDBACK);
      if (storedAssignees) {
        setAssignees(JSON.parse(storedAssignees));
      }
      if (storedFeedback) {
        setCandidateFeedback(JSON.parse(storedFeedback));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, [STORAGE_KEY_ASSIGNEES, STORAGE_KEY_FEEDBACK]);

  const fetchCandidates = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${serverURL}/reports/registered/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCandidates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch candidates:', error);
      setCandidates([]);
    }
  }, [eventId]);

  useEffect(() => {
    loadData();
    fetchCandidates();
  }, [fetchCandidates, loadData]);

  const saveAssignees = (updated) => {
    try {
      localStorage.setItem(STORAGE_KEY_ASSIGNEES, JSON.stringify(updated));
      setAssignees(updated);
    } catch (error) {
      console.error('Failed to save assignees:', error);
      alert('Failed to save assignee');
    }
  };

  const saveFeedback = (updated) => {
    try {
      localStorage.setItem(STORAGE_KEY_FEEDBACK, JSON.stringify(updated));
      setCandidateFeedback(updated);
    } catch (error) {
      console.error('Failed to save feedback:', error);
      alert('Failed to save feedback');
    }
  };

  const handleAddAssignee = () => {
    if (!newAssignee.interviewer_name || !newAssignee.interviewer_email) {
      alert('Please fill in all fields');
      return;
    }
    const assignee = { id: Date.now(), ...newAssignee };
    saveAssignees([...assignees, assignee]);
    setNewAssignee({ interviewer_name: '', interviewer_email: '', interview_type: 'Online' });
    setIsAssigneeModalOpen(false);
  };

  const handleRemoveAssignee = (id) => {
    if (window.confirm('Are you sure you want to remove this interviewer?')) {
      saveAssignees(assignees.filter((a) => a.id !== id));
    }
  };

  const handleSelectCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    const key = `candidate_${candidate.id}`;
    if (candidateFeedback[key]) {
      setFeedbackForm(candidateFeedback[key]);
    } else {
      setFeedbackForm({ feedback_title: '', feedback_text: '', rating: 3 });
    }
  };

  const handleSubmitFeedback = () => {
    if (!selectedCandidate) {
      alert('Please select a candidate');
      return;
    }
    if (!feedbackForm.feedback_title || !feedbackForm.feedback_text) {
      alert('Please fill in feedback title and text');
      return;
    }
    const key = `candidate_${selectedCandidate.id}`;
    const updated = {
      ...candidateFeedback,
      [key]: {
        ...feedbackForm,
        submitted_at: new Date().toISOString(),
        candidate_id: selectedCandidate.id,
      },
    };
    saveFeedback(updated);
    setIsFeedbackModalOpen(false);
    alert('Feedback submitted successfully');
  };

  const filteredCandidates = candidates.filter(
    (candidate) =>
      candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCandidateFeedback = (candidateId) => {
    const key = `candidate_${candidateId}`;
    return candidateFeedback[key];
  };

  const renderStars = (rating) => {
    const filledStars = '★'.repeat(rating);
    const emptyStars = '☆'.repeat(5 - rating);
    return (
      <span className="text-yellow-500 text-lg">
        {filledStars}
        {emptyStars}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-5 flex items-center justify-center h-48">
        <p className="text-sm text-campus-text-body animate-pulse">Loading feedback management…</p>
      </div>
    );
  }

  const inputClass = "w-full px-3 py-2 text-sm border border-campus-border rounded-lg bg-campus-surface text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary transition-all";
  const labelClass = "block text-xs font-semibold text-campus-text-body uppercase tracking-wide mb-1.5";

  return (
    <div className="p-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* ── Interviewers panel ───────────────────────────────── */}
        <div className="lg:col-span-1">
          <div className="bg-campus-surface border border-campus-border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-campus-border flex items-center justify-between">
              <p className="text-xs font-semibold text-campus-text-body uppercase tracking-wide">
                Interviewers ({assignees.length})
              </p>
              <button
                onClick={() => setIsAssigneeModalOpen(true)}
                className="p-1.5 rounded-lg text-campus-text-body hover:text-campus-primary hover:bg-campus-bg transition-colors"
                title="Add interviewer"
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-campus-border">
              {assignees.length === 0 ? (
                <div className="p-5 text-center">
                  <p className="text-sm text-campus-text-body">No interviewers assigned yet</p>
                </div>
              ) : (
                assignees.map((assignee) => (
                  <div
                    key={assignee.id}
                    className="px-4 py-3 hover:bg-campus-bg transition-colors flex items-start justify-between gap-2"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-campus-text-primary truncate">{assignee.interviewer_name}</p>
                      <p className="text-xs text-campus-text-body truncate">{assignee.interviewer_email}</p>
                      <span className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                        {assignee.interview_type}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRemoveAssignee(assignee.id)}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-campus-text-body hover:text-campus-error transition-colors flex-shrink-0"
                      title="Remove interviewer"
                    >
                      <FontAwesomeIcon icon={faTrash} className="text-xs" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ── Candidates + Feedback panel ────────────────────────── */}
        <div className="lg:col-span-2 space-y-4">

          {/* Search */}
          <div className="bg-campus-surface border border-campus-border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-campus-border">
              <div className="relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-campus-text-body text-sm" />
                <input
                  type="text"
                  placeholder="Search candidates…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-campus-border rounded-lg bg-campus-bg text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary transition-all"
                />
              </div>
            </div>

            <div className="max-h-72 overflow-y-auto divide-y divide-campus-border">
              {filteredCandidates.length === 0 ? (
                <div className="p-5 text-center">
                  <p className="text-sm text-campus-text-body">
                    {candidates.length === 0
                      ? 'No candidates registered for this event'
                      : 'No matching candidates'}
                  </p>
                </div>
              ) : (
                filteredCandidates.map((candidate) => {
                  const feedback = getCandidateFeedback(candidate.id);
                  return (
                    <button
                      key={candidate.id}
                      type="button"
                      className={`w-full text-left px-4 py-3 hover:bg-campus-bg transition-colors duration-150 border-l-4 ${selectedCandidate?.id === candidate.id
                          ? 'border-l-campus-primary bg-campus-bg'
                          : 'border-l-transparent'
                        }`}
                      onClick={() => handleSelectCandidate(candidate)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-campus-text-primary truncate">{candidate.name}</p>
                          <p className="text-xs text-campus-text-body truncate">{candidate.email}</p>
                          {feedback && (
                            <div className="mt-1.5 flex items-center gap-1.5">
                              {renderStars(feedback.rating)}
                              <span className="text-xs text-campus-text-body">({feedback.rating}/5)</span>
                            </div>
                          )}
                        </div>
                        {feedback && (
                          <span className="flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                            Reviewed
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Selected candidate feedback */}
          {selectedCandidate && (
            <div className="bg-campus-surface border border-campus-border rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-campus-border flex items-center justify-between">
                <p className="text-sm font-semibold text-campus-text-primary truncate">
                  Feedback — {selectedCandidate.name}
                </p>
                <button
                  onClick={() => setIsFeedbackModalOpen(true)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-campus-primary hover:bg-campus-primary-dark text-white rounded-lg transition-colors whitespace-nowrap"
                >
                  {getCandidateFeedback(selectedCandidate.id) ? 'Edit Feedback' : 'Add Feedback'}
                </button>
              </div>
              {getCandidateFeedback(selectedCandidate.id) ? (
                <div className="p-4">
                  <p className="text-sm font-semibold text-campus-text-primary mb-1">
                    {getCandidateFeedback(selectedCandidate.id).feedback_title}
                  </p>
                  <div className="mb-2">{renderStars(getCandidateFeedback(selectedCandidate.id).rating)}</div>
                  <p className="text-sm text-campus-text-body whitespace-pre-wrap">
                    {getCandidateFeedback(selectedCandidate.id).feedback_text}
                  </p>
                  <p className="text-xs text-campus-text-body mt-3">
                    Submitted: {new Date(getCandidateFeedback(selectedCandidate.id).submitted_at).toLocaleString()}
                  </p>
                </div>
              ) : (
                <div className="p-5 text-center">
                  <p className="text-sm text-campus-text-body">No feedback submitted yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Add Interviewer Modal ──────────────────────────────────── */}
      {isAssigneeModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-campus-surface rounded-2xl shadow-hover max-w-md w-full mx-4">
            <div className="p-5 border-b border-campus-border flex items-center justify-between">
              <h3 className="text-base font-heading font-bold text-campus-text-primary">Add Interviewer</h3>
              <button
                onClick={() => setIsAssigneeModalOpen(false)}
                className="p-1.5 rounded-lg text-campus-text-body hover:bg-campus-bg transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-sm" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label htmlFor="assignee-name" className={labelClass}>Interviewer Name *</label>
                <input
                  id="assignee-name"
                  type="text"
                  value={newAssignee.interviewer_name}
                  onChange={(e) => setNewAssignee({ ...newAssignee, interviewer_name: e.target.value })}
                  className={inputClass}
                  placeholder="Enter name"
                />
              </div>
              <div>
                <label htmlFor="assignee-email" className={labelClass}>Email *</label>
                <input
                  id="assignee-email"
                  type="email"
                  value={newAssignee.interviewer_email}
                  onChange={(e) => setNewAssignee({ ...newAssignee, interviewer_email: e.target.value })}
                  className={inputClass}
                  placeholder="Enter email"
                />
              </div>
              <div>
                <label htmlFor="assignee-interview-type" className={labelClass}>Interview Type</label>
                <select
                  id="assignee-interview-type"
                  value={newAssignee.interview_type}
                  onChange={(e) => setNewAssignee({ ...newAssignee, interview_type: e.target.value })}
                  className={inputClass}
                >
                  <option value="Online">Online</option>
                  <option value="Offline">Offline</option>
                  <option value="Phone">Phone</option>
                </select>
              </div>
            </div>
            <div className="p-5 border-t border-campus-border flex justify-end gap-2">
              <button
                onClick={() => setIsAssigneeModalOpen(false)}
                className="px-4 py-2 text-sm border border-campus-border text-campus-text-body rounded-lg hover:bg-campus-bg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAssignee}
                className="px-4 py-2 text-sm bg-campus-primary hover:bg-campus-primary-dark text-white rounded-lg transition-colors font-semibold"
              >
                Add Interviewer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Feedback Modal ────────────────────────────────────────── */}
      {isFeedbackModalOpen && selectedCandidate && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-campus-surface rounded-2xl shadow-hover max-w-md w-full mx-4">
            <div className="p-5 border-b border-campus-border flex items-center justify-between">
              <div>
                <h3 className="text-base font-heading font-bold text-campus-text-primary">Feedback</h3>
                <p className="text-xs text-campus-text-body mt-0.5">{selectedCandidate.name}</p>
              </div>
              <button
                onClick={() => setIsFeedbackModalOpen(false)}
                className="p-1.5 rounded-lg text-campus-text-body hover:bg-campus-bg transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-sm" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label htmlFor="feedback-title" className={labelClass}>Title *</label>
                <input
                  id="feedback-title"
                  type="text"
                  value={feedbackForm.feedback_title}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, feedback_title: e.target.value })}
                  className={inputClass}
                  placeholder="Enter feedback title"
                />
              </div>
              <div>
                <label htmlFor="feedback-text" className={labelClass}>Feedback *</label>
                <textarea
                  id="feedback-text"
                  value={feedbackForm.feedback_text}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, feedback_text: e.target.value })}
                  className={`${inputClass} resize-none h-24`}
                  placeholder="Enter detailed feedback"
                />
              </div>
              <div>
                <label htmlFor="feedback-rating" className={labelClass}>Rating</label>
                <div id="feedback-rating" className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFeedbackForm({ ...feedbackForm, rating: star })}
                      className={`text-2xl transition-colors ${star <= feedbackForm.rating ? 'text-amber-400' : 'text-campus-border'
                        }`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-campus-border flex justify-end gap-2">
              <button
                onClick={() => setIsFeedbackModalOpen(false)}
                className="px-4 py-2 text-sm border border-campus-border text-campus-text-body rounded-lg hover:bg-campus-bg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitFeedback}
                className="px-4 py-2 text-sm bg-campus-primary hover:bg-campus-primary-dark text-white rounded-lg transition-colors font-semibold"
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

AssigneeFeedbackList.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  levelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default AssigneeFeedbackList;
